/**
 * Módulos contratables por comité — espejo de `backend/app/core/modulos.py`.
 *
 * Un permiso pertenece al módulo de su prefijo (`lecturas.tomar` → lecturas).
 * Si el JWT trae `modulos` (comité sin plan completo), `hasPermission` niega
 * los permisos de módulos no contratados y el menú los oculta. El backend los
 * exige igual: esto es conveniencia visual, no seguridad.
 */

export type ModuloCode =
  | "padron"
  | "lecturas"
  | "facturacion"
  | "asambleas"
  | "atencion"
  | "continuidad"
  | "operaciones"
  | "contabilidad";

const PREFIJOS: Record<ModuloCode, readonly string[]> = {
  padron: ["socios", "sectores", "arranques", "rutas", "medidores", "contratos"],
  lecturas: ["lecturas"],
  facturacion: [
    "tarifas",
    "cierres",
    "entregas",
    "pagos",
    "caja",
    "ajustes",
    "multas",
    "cortes",
    "subsidios",
  ],
  asambleas: ["asambleas"],
  atencion: ["atenciones"],
  continuidad: ["continuidad"],
  operaciones: ["inventario", "compras", "mantenimiento"],
  contabilidad: ["contabilidad"],
};

const PREFIJO_A_MODULO: ReadonlyMap<string, ModuloCode> = new Map(
  (Object.entries(PREFIJOS) as [ModuloCode, readonly string[]][]).flatMap(
    ([modulo, prefijos]) => prefijos.map((p) => [p, modulo] as const),
  ),
);

/** Módulo al que pertenece el permiso, o null si es del núcleo. */
export function moduloDePermiso(permiso: string): ModuloCode | null {
  const prefijo = permiso.split(".", 1)[0] ?? "";
  return PREFIJO_A_MODULO.get(prefijo) ?? null;
}

/** False si el permiso es de un módulo que el comité no contrató. */
export function permisoHabilitado(
  permiso: string,
  modulos: readonly string[] | undefined,
): boolean {
  if (!modulos) return true;
  const modulo = moduloDePermiso(permiso);
  return modulo === null || modulos.includes(modulo);
}

/** True si el comité tiene el módulo (plan completo = todos). */
export function tieneModulo(
  modulos: readonly string[] | undefined,
  modulo: ModuloCode,
): boolean {
  return !modulos || modulos.includes(modulo);
}
